import React from "react";
import gql from "graphql-tag";
import striptags from "striptags";
import { Box, IBoxProps, Text, H3, TouchableOpacity } from "@atoms";
import { renderIf } from "@services";
import { useToggle } from "@hooks";
import { LinkModalHtmlTerms } from "../LinkModalHtmlTerms";
import { LinkModalTermsFragment } from "./__generated__/LinkModalTermsFragment";

// import styled from "@emotion/styled";

// == Types ================================================================

interface IProps extends IBoxProps {
  link: LinkModalTermsFragment;
}

// == Constants ============================================================

const FRAGMENTS = {
  fields: gql`
    fragment LinkModalTermsFragment on Link {
      id
      exclusions
      exclusionGiftCardNotEligible
      exclusionPurchaseWithGiftCardNotEligible
      variableRates
      terms
      termsSummary
    }
  `,
};

// == Component ============================================================

const LinkModalTerms = ({ link, ...rest }: IProps) => {
  const [showFullTerms, toggleShowFullTerms] = useToggle(true);
  const shouldShowExclusions = () => {
    return (
      !!link.exclusions ||
      !!link.exclusionGiftCardNotEligible ||
      !!link.exclusionPurchaseWithGiftCardNotEligible
    );
  };

  return (
    <Box {...rest}>
      {shouldShowExclusions() && (
        <Box mb={3}>
          <H3>Exclusions</H3>
          {!!link.exclusions && <LinkModalHtmlTerms source={link.exclusions} />}

          {!!link.exclusionGiftCardNotEligible && (
            <Text display="block" mt={1} textStyle="legal">
              Gift cards are not eligible for cash back.
            </Text>
          )}

          {!!link.exclusionPurchaseWithGiftCardNotEligible && (
            <Text display="block" mt={1} textStyle="legal">
              Purchases made with a gift card are not eligible for cash back.
            </Text>
          )}
        </Box>
      )}
      {renderIf(!!link.variableRates)(
        <Box mb={3}>
          <H3>Variable Cash Back Rates</H3>
          <LinkModalHtmlTerms source={link.variableRates} />
        </Box>
      )}
      {renderIf(!!link.terms || !!link.termsSummary)(
        <Box mb={3}>
          <H3>Terms & Conditions</H3>
          {renderIf(!!link.termsSummary)(
            <Text display="block" textStyle="legal">
              <LinkModalHtmlTerms source={link.termsSummary} />
            </Text>
          )}
          {renderIf(!!link.terms)(
            <>
              {renderIf(showFullTerms)(<LinkModalHtmlTerms source={link.terms} />)}
              <TouchableOpacity onTouch={toggleShowFullTerms}>
                <Text color="primary" display="block" mb={2} textStyle="legal">
                  {showFullTerms ? "Hide" : "See"} full terms & conditions
                </Text>
              </TouchableOpacity>
            </>
          )}
        </Box>
      )}
      <Box>
        <H3>To earn cash back:</H3>
        <ul className="text-tiny browser-default mb-0">
          <li>
            <Text>
              Only use coupon codes advertised here. The use of codes from other websites can void
              your cash back
            </Text>
          </li>
          <li>
            <Text>
              Do not pay with Loyalty Points or Rewards Cash. This will void your cash back
            </Text>
          </li>
        </ul>
      </Box>
    </Box>
  );
};

LinkModalTerms.fragments = FRAGMENTS;

// == Styles ===============================================================
export default LinkModalTerms;
